/**
 * klubb-picker — modal open/close, search over the club directory.
 * ESM module (loaded with type="module"); auto-inits in the browser and
 * exports pure helpers for unit tests.
 *
 * The club directory in the picker (R2, D70). The picker is in every page, so the page holds no
 * tile for any club. When it opens it takes the club directory (window.klubb.directory(): the
 * paged club feed, cached in the browser) and draws an assortment of it: the first PAGE_SIZE
 * clubs of the day's shuffle (the same order all day for everybody, a new one tomorrow),
 * «Vis flere» adds a page, so a visitor sees at once that there are many clubs.
 * Typing, or choosing a county (fylke), narrows the same list. Tiles are built here; the tile model is
 * the one of klubb-crest.liquid.
 */

export const PAGE_SIZE = 24;

export function formatResultNote(count, query) {
  return count + ' treff for «' + query + '»';
}

export function markSelected(scope, slug) {
  const tiles = scope.querySelectorAll('[data-klubb-picker-tile]');
  tiles.forEach((tile) => {
    tile.classList.toggle('is-selected', !!slug && tile.dataset.slug === slug);
  });
}

/* ---------------- search over the directory (pure) ---------------- */

// Lower case without accents, æ ø å as ae o a and aa as a, so «mjondalen» finds «Mjøndalen» and «Ålesund» finds «Aalesund».
export function fold(s) {
  return String(s == null ? '' : s)
    .toLowerCase()
    .replace(/æ/g, 'ae').replace(/ø/g, 'o').replace(/å/g, 'a')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/aa/g, 'a')
    .trim();
}

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nb');

/* ---------------- the day's order (pure) ---------------- */

// 20261029 for 29 October 2026 (the browser's own date).
export function daySeed(date) {
  const d = date || new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

// A small seeded random number generator (mulberry32): the same seed gives the same numbers on every device.
function seededRandom(seed) {
  let a = seed | 0;
  return function () {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The clubs in a shuffled order that depends only on the seed (use daySeed()) and on which clubs
 * there are, not on the order they arrive in: everybody sees the same list all day, and tomorrow
 * a new one. The clubs are put in slug order first so that the feed's own order does not matter.
 */
export function shuffleDaily(clubs, seed) {
  const list = (clubs || []).filter(Boolean).sort((a, b) => (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0));
  const random = seededRandom(seed);
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const t = list[i]; list[i] = list[j]; list[j] = t;
  }
  return list;
}

/**
 * The clubs whose name, short name or county (fylke) contain every word of the query, best first:
 * short name equal to the query, then name starting with it, then a name word starting with it,
 * then the rest of the name matches, then the clubs that match only through their county; at
 * equal rank the clubs spelled exactly like the query (ø, å and æ as typed) come first, then by
 * name. «viken fotball» finds the football clubs of Viken. An empty query keeps the order it is given.
 */
export function rankClubs(clubs, query) {
  const list = (clubs || []).filter(Boolean);
  const q = fold(query);
  if (!q) return list.slice();
  const words = q.split(/\s+/).filter(Boolean);
  const typed = String(query).toLowerCase().trim();
  const scored = [];
  for (const c of list) {
    const name = fold(c.name);
    const short = fold(c.short_name);
    const hay = name + ' ' + short;
    const county = fold(c.region);
    const inName = words.every((w) => hay.indexOf(w) !== -1);
    if (!inName && !words.every((w) => (hay + ' ' + county).indexOf(w) !== -1)) continue;
    let score = 4; // matches only together with the county
    if (inName) {
      score = 3;
      if (short === q) score = 0;
      else if (name.indexOf(q) === 0) score = 1;
      else if (name.split(/[\s\-–]+/).some((part) => part.indexOf(words[0]) === 0)) score = 2;
    }
    // the spelling counts where the query matched: the name (and short name), or the county for a county-only match
    const written = String(c.name || '') + ' ' + String(c.short_name || '') + (inName ? '' : ' ' + String(c.region || ''));
    const spelled = written.toLowerCase().indexOf(typed) !== -1 ? 0 : 1;
    scored.push({ c, score, spelled });
  }
  scored.sort((a, b) => a.score - b.score || a.spelled - b.spelled || byName(a.c, b.c));
  return scored.map((x) => x.c);
}

/**
 * { shown, total }: the best `limit` matches of `query` in `region` ('Alle' or blank = every region), and
 * how many match. Always over every club it is given, never only the ones already drawn. A county
 * chosen with nothing typed lists its clubs by name, so a member can find theirs; with nothing chosen
 * and nothing typed the order is the one given (the day's shuffle).
 */
export function filterClubs(clubs, opts) {
  const o = opts || {};
  const region = o.region;
  const countyChosen = !!region && region !== 'Alle';
  const inRegion = (clubs || []).filter((c) => c && (!countyChosen || c.region === region));
  const ranked = rankClubs(inRegion, o.query);
  if (countyChosen && !fold(o.query)) ranked.sort(byName);
  const max = o.limit == null ? PAGE_SIZE : o.limit;
  return { shown: ranked.slice(0, max), total: ranked.length };
}

/** The regions of the clubs, each once, in Norwegian order. */
export function regionsOf(clubs) {
  const seen = {};
  const out = [];
  (clubs || []).forEach((c) => {
    const r = c && c.region;
    if (r && !seen[r]) { seen[r] = true; out.push(r); }
  });
  return out.sort((a, b) => a.localeCompare(b, 'nb'));
}

/* ---------------- one tile ---------------- */

// What a tile shows, from a club record (the same decisions as snippets/klubb-crest.liquid).
export function tileModel(club) {
  const ratio = Number(club.crest_image_ratio) || 0;
  const size = 44;
  return {
    slug: club.slug,
    name: club.name || '',
    region: club.region || '',
    partner: !!club.partner_store_name,
    members: !club.partner_store_name && Number(club.members_count) > 0 ? Number(club.members_count) : 0,
    preview: !!club.preview_only,
    crest: {
      size,
      fontSize: Math.round(size * 0.32),
      background: club.primary_color || '#0a0a0a',
      color: club.secondary_color || '#ffffff',
      imageUrl: club.crest_image_url || '',
      imageClass: ratio >= 0.75 && ratio <= 1.35 ? 'klubb-crest__img klubb-crest__img--cover' : 'klubb-crest__img',
      initials: String(club.crest_initials || club.short_name || '').slice(0, 2).toUpperCase(),
    },
  };
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** A tile for the grid. labels: { members, partner, preview, selected }. */
export function buildTile(doc, club, labels) {
  const m = tileModel(club);
  const L = labels || {};
  const el = (tag, cls) => { const e = doc.createElement(tag); if (cls) e.className = cls; return e; };

  const tile = el('button', 'klubb-picker__tile');
  tile.type = 'button';
  tile.setAttribute('data-klubb-picker-tile', '');
  tile.setAttribute('data-slug', m.slug);

  const crest = el('div', 'klubb-crest');
  crest.setAttribute('role', 'img');
  crest.setAttribute('aria-label', m.name);
  crest.setAttribute('style', 'width: ' + m.crest.size + 'px; height: ' + m.crest.size + 'px; background: ' + m.crest.background +
    '; color: ' + m.crest.color + '; font-size: ' + m.crest.fontSize + 'px;');
  if (m.crest.imageUrl) {
    const img = el('img', m.crest.imageClass);
    img.setAttribute('src', m.crest.imageUrl);
    img.setAttribute('alt', m.name);
    img.setAttribute('loading', 'lazy');
    crest.appendChild(img);
  } else {
    const initials = el('span', 'klubb-crest__initials');
    initials.textContent = m.crest.initials;
    crest.appendChild(initials);
  }
  tile.appendChild(crest);

  const text = el('div', 'klubb-picker__tile-text');
  const name = el('span', 'klubb-picker__tile-name');
  name.appendChild(doc.createTextNode(m.name));
  if (m.preview) {
    const badge = el('span', 'klubb-picker__tile-preview');
    badge.textContent = L.preview || '';
    name.appendChild(badge);
  }
  text.appendChild(name);
  const meta = el('span', 'klubb-picker__tile-meta');
  meta.appendChild(doc.createTextNode(m.region));
  if (m.partner) {
    meta.appendChild(doc.createTextNode(' · '));
    const strong = el('strong');
    strong.textContent = L.partner || '';
    meta.appendChild(strong);
  } else if (m.members > 0) {
    meta.appendChild(doc.createTextNode(' · ' + m.members + ' ' + (L.members || '')));
  }
  text.appendChild(meta);
  tile.appendChild(text);

  const chevron = el('span', 'klubb-picker__tile-chevron');
  chevron.setAttribute('aria-hidden', 'true');
  const svg = doc.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'klubb-icon');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const use = doc.createElementNS(SVG_NS, 'use');
  use.setAttribute('href', '#klubb-icon-arrow');
  svg.appendChild(use);
  chevron.appendChild(svg);
  tile.appendChild(chevron);

  const valgt = el('span', 'klubb-picker__tile-valgt');
  valgt.setAttribute('aria-hidden', 'true');
  valgt.textContent = L.selected || '';
  tile.appendChild(valgt);
  return tile;
}

// D47 — stay on the product page across a club switch when the product is
// eligible for the newly selected club (same matcher + semantics as cart
// reconciliation: blank token list = general product, eligible for all).
// Missing matcher/club/template → false, so the caller falls back to the
// existing catalog navigation and the member is never stranded.
export function stayOnPdp(doc, club, matcher) {
  if (typeof matcher !== 'function' || !club) return false;
  const main = doc.querySelector('[data-template]');
  const template = main ? main.getAttribute('data-template') || '' : '';
  if (template !== 'product' && template.indexOf('product.') !== 0) return false;
  const input = main.querySelector('[data-klubb-eligible-clubs]');
  return matcher(input ? input.value : '', club);
}

/**
 * Fills the «Din klubb» hub card on the picker page from the active club.
 * Server-rendered hidden (Liquid can't read the cookie); reveals only when a
 * club is active. Returns true when the card was shown.
 */
export function hydrateMine(doc, club) {
  const card = doc.querySelector('[data-klubb-picker-mine]');
  if (!card) return false;
  if (!club) { card.hidden = true; return false; }

  const name = card.querySelector('[data-klubb-picker-mine-name]');
  if (name) name.textContent = club.name || club.short_name || '';

  const cont = card.querySelector('[data-klubb-picker-mine-continue]');
  if (cont) {
    cont.href = club.catalog_collection_handle
      ? '/collections/' + club.catalog_collection_handle
      : '/';
  }

  const crest = card.querySelector('[data-klubb-picker-mine-crest]');
  if (crest) {
    crest.textContent = '';
    const el = doc.createElement('div');
    el.className = 'klubb-crest klubb-crest--shield';
    // Size comes from CSS (.klubb-picker-page__mine-crest) so mobile can shrink it.
    el.style.cssText = 'background:' + (club.primary_color || '#0a0a0a') +
      ';color:' + (club.secondary_color || '#ffffff');
    if (club.crest_image_url) {
      const img = doc.createElement('img');
      const ratio = Number(club.crest_image_ratio) || 0;
      img.className = ratio >= 0.75 && ratio <= 1.35 ? 'klubb-crest__img klubb-crest__img--cover' : 'klubb-crest__img';
      img.src = club.crest_image_url;
      img.alt = '';
      el.appendChild(img);
    } else {
      const span = doc.createElement('span');
      span.className = 'klubb-crest__initials';
      span.textContent = (club.crest_initials || club.short_name || '').slice(0, 2).toUpperCase();
      el.appendChild(span);
    }
    crest.appendChild(el);
  }

  card.hidden = false;
  return true;
}

(function () {
  'use strict';

  let initialized = false;
  const scopes = [];

  function init() {
    if (initialized) return;
    initialized = true;

    const modals = document.querySelectorAll('[data-klubb-picker-modal]');

    document.addEventListener('klubb:picker:open', () => {
      modals.forEach((m) => {
        m.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        const search = m.querySelector('[data-klubb-picker-search]');
        if (search) setTimeout(() => search.focus(), 50);
      });
      // Start fetching the club directory now: by the time the member types it is usually there.
      scopes.forEach((s) => s.preload());
    });

    document.addEventListener('klubb:picker:close', () => {
      modals.forEach((m) => { m.hidden = true; });
      document.documentElement.style.overflow = '';
    });

    // Wire close buttons + overlay clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-klubb-picker-close]')) {
        window.klubb && window.klubb.closePicker();
        return;
      }
      const overlay = e.target.closest('[data-klubb-picker-modal]');
      const inner = e.target.closest('.klubb-picker');
      if (overlay && !inner) {
        window.klubb && window.klubb.closePicker();
      }
    });

    // Wire ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') window.klubb && window.klubb.closePicker();
    });

    // Wire all picker scopes (modal + page)
    document.querySelectorAll('[data-klubb-picker-search]').forEach((input) => {
      const scope = input.closest('.klubb-picker');
      if (!scope) return;
      scopes.push(wireScope(scope));
    });

    // The member's club first (a first visit without a stored copy looks it up): selected tile, «Fjern valgt klubb», the hub card.
    const k = window.klubb;
    Promise.resolve(k && typeof k.ready === 'function' ? k.ready() : null).catch(() => null).then(() => {
      scopes.forEach((s) => s.refreshSelection());
      if (hydrateMine(document, k ? k.activeClub : null)) {
        const change = document.querySelector('[data-klubb-picker-mine-change]');
        const search = document.querySelector('.klubb-picker-page [data-klubb-picker-search]');
        if (change) {
          change.addEventListener('click', () => {
            const picker = document.querySelector('.klubb-picker-page .klubb-picker');
            if (picker && picker.scrollIntoView) picker.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (search) setTimeout(() => search.focus({ preventScroll: true }), 300);
          });
        }
      }
    });
    if (k && typeof k.on === 'function') k.on('change', () => scopes.forEach((s) => s.refreshSelection()));
  }

  function wireScope(scope) {
    const search = scope.querySelector('[data-klubb-picker-search]');
    const clear = scope.querySelector('[data-klubb-picker-clear]');
    const filters = scope.querySelector('[data-klubb-picker-filters]');
    const list = scope.querySelector('[data-klubb-picker-list]');
    const empty = scope.querySelector('[data-klubb-picker-empty]');
    const note = scope.querySelector('[data-klubb-picker-result-note]');
    const loadingEl = scope.querySelector('[data-klubb-picker-loading]');
    const failedEl = scope.querySelector('[data-klubb-picker-failed]');
    const moreBtn = scope.querySelector('[data-klubb-picker-more]');
    const countSummary = scope.querySelector('[data-klubb-picker-count-summary]');
    const visibleCount = scope.querySelector('[data-klubb-picker-visible-count]');
    const totalCount = scope.querySelector('[data-klubb-picker-total-count]');
    const body = scope.querySelector('.klubb-picker__body');
    const labels = {
      members: body ? body.getAttribute('data-label-members') : '',
      partner: body ? body.getAttribute('data-label-partner') : '',
      preview: body ? body.getAttribute('data-label-preview') : '',
      selected: body ? body.getAttribute('data-label-selected') : '',
    };

    let query = '';
    let region = 'Alle';
    let limit = PAGE_SIZE; // how many matches are drawn; «Vis flere» adds a page
    let directoryClubs = null;
    let loading = false;
    let loadFailed = false;

    const show = (el, on) => { if (el) el.hidden = !on; };

    function setTiles(nodes) {
      if (!list) return;
      while (list.firstChild) list.removeChild(list.firstChild);
      nodes.forEach((n) => list.appendChild(n));
    }

    function refreshSelection() {
      const k = window.klubb;
      markSelected(scope, k && k.activeClub ? k.activeClub.slug : null);
      // Deselect — only visible when a club is currently active.
      const deselect = scope.querySelector('[data-klubb-picker-deselect]');
      if (deselect) deselect.hidden = !(k && k.activeClub);
    }

    // «24 av 50»: how many are drawn of the clubs in the current view (every club, or the county's, or the search's).
    function updateCount(shownCount, matchCount) {
      if (!directoryClubs) return;
      if (visibleCount) visibleCount.textContent = shownCount;
      if (totalCount) totalCount.textContent = matchCount;
      show(countSummary, true);
    }

    function addRegionChips(all) {
      if (!filters) return;
      regionsOf(all).forEach((r) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'klubb-picker__filter-chip';
        chip.setAttribute('data-region', r);
        chip.textContent = r;
        filters.appendChild(chip);
      });
    }

    // Takes the club directory once (the browser keeps a copy), then draws. A failed read is shown
    // and tried again when the member next types in or focuses the search box, never in a loop.
    function loadDirectory() {
      const k = window.klubb;
      if (directoryClubs || loading) return;
      if (!k || typeof k.directory !== 'function') { loadFailed = true; render(); return; }
      loading = true;
      loadFailed = false;
      render();
      k.directory().then((all) => {
        loading = false;
        if (all && all.length) { directoryClubs = shuffleDaily(all, daySeed()); addRegionChips(all); } else { loadFailed = true; }
        render();
      }).catch(() => { loading = false; loadFailed = true; render(); });
    }

    function render() {
      show(clear, query !== '');
      if (!directoryClubs) {
        // The club directory is on its way (or could not be read): nothing to draw yet.
        setTiles([]);
        show(empty, false); show(note, false); show(moreBtn, false);
        show(loadingEl, loading);
        show(failedEl, loadFailed);
        return;
      }
      show(loadingEl, false); show(failedEl, false);
      const found = filterClubs(directoryClubs, { query, region, limit });
      setTiles(found.shown.map((c) => buildTile(document, c, labels)));
      show(empty, found.shown.length === 0);
      show(moreBtn, found.shown.length < found.total);
      if (query !== '' && found.total > 0) {
        if (note) note.textContent = formatResultNote(found.total, query);
        show(note, true);
      } else {
        show(note, false);
      }
      updateCount(found.shown.length, found.total);
      refreshSelection();
    }

    if (search) {
      search.addEventListener('input', (e) => {
        query = (e.target.value || '').trim();
        limit = PAGE_SIZE;
        loadDirectory();
        render();
      });
      search.addEventListener('focus', loadDirectory);
    }
    if (clear) {
      clear.addEventListener('click', () => {
        if (search) search.value = '';
        query = '';
        limit = PAGE_SIZE;
        render();
        if (search) search.focus();
      });
    }
    if (filters) {
      filters.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-region]');
        if (!chip) return;
        filters.querySelectorAll('[data-region]').forEach((c) => c.classList.remove('is-active'));
        chip.classList.add('is-active');
        region = chip.getAttribute('data-region');
        limit = PAGE_SIZE;
        render();
      });
    }
    if (moreBtn) {
      moreBtn.addEventListener('click', () => { limit += PAGE_SIZE; render(); });
    }

    // Commit a club switch: write the cookie, close the picker, and navigate to
    // the club's catalog (or reload to re-render with the new club context).
    function doSwitch(slug) {
      window.klubb.setActiveClub(slug);
      window.klubb.closePicker();
      const c = window.klubb.activeClub;
      // D47: on a PDP whose product is eligible for the new club, reload in
      // place instead of navigating away — the re-render picks up the new
      // club context. Ineligible or non-PDP → catalog as before.
      const matcher = window.klubbCartReconcile && window.klubbCartReconcile.lineEligibleForClub;
      if (c && stayOnPdp(document, c, matcher)) {
        window.location.reload();
        return;
      }
      if (c && c.catalog_collection_handle) {
        window.location.href = '/collections/' + c.catalog_collection_handle;
      } else {
        window.location.reload();
      }
    }

    // A tile carries only the slug: take the club record from the directory (the browser already has it) before switching.
    function choose(slug, tile) {
      const k = window.klubb;
      if (!slug || !k) return;
      if (tile) tile.setAttribute('aria-busy', 'true');
      Promise.resolve(k.whenClub(slug)).catch(() => null).then((club) => {
        if (tile) tile.removeAttribute('aria-busy');
        if (!club) { show(failedEl, true); return; }
        show(failedEl, false);
        const current = k.activeClub;
        // D45: switching away from an existing, different club reconciles the
        // cart first — an itemized confirm dialog, then remove ineligible lines
        // and re-stamp the rest — BEFORE the switch commits. A cancel keeps the
        // member on their current club. Falls back to a plain switch when the
        // reconcile module isn't present or errors.
        if (current && current.slug !== slug && window.klubbCartReconcile && window.klubbCartReconcile.run) {
          window.klubbCartReconcile.run(slug).then((res) => {
            if (res && res.switched === false) return;
            doSwitch(slug);
          }).catch(() => doSwitch(slug));
          return;
        }
        doSwitch(slug);
      });
    }

    // Tile selection (the tiles come and go with the search, so one listener on the grid)
    if (list) {
      list.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-klubb-picker-tile]');
        if (tile) choose(tile.dataset.slug, tile);
      });
    }

    // Deselect — clears the cookie + window.klubb state and reloads back to the no-club home.
    const deselect = scope.querySelector('[data-klubb-picker-deselect]');
    if (deselect) {
      deselect.addEventListener('click', () => {
        if (!window.klubb) return;
        window.klubb.clearActiveClub();
        window.klubb.closePicker();
        window.location.href = '/';
      });
    }

    render();
    // The modal takes the directory when it opens; the picker page is open from the start.
    if (scope.closest('.klubb-picker-page')) loadDirectory();

    return { preload: loadDirectory, refreshSelection };
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
